import { QueryFunction, useQuery } from '@tanstack/react-query';
import { useSiloConfiguration } from '@silo/config-legacy';
import { ChecksumAddress, ClientProvider, ProtocolInstance, getChecksumAddressOrThrow } from '@silo/types';
import { useSiloWeb3 } from '@silo/web3';
import { createSiloContract, createSiloRepositoryContract } from '@silo/contract-utils';

type IsPausedKey = [
  key: 'isPaused',
  clientProvider: ClientProvider,
  protocolInstance: ProtocolInstance,
  siloAddress: ChecksumAddress
];

export const isPausedKey: IsPausedKey[0] = 'isPaused';

const queryIsPaused: QueryFunction<boolean, IsPausedKey> = async ({ queryKey }) => {
  const [, clientProvider, protocolInstance, siloAddress] = queryKey;

  const siloContract = createSiloContract({ siloAddress, provider: clientProvider });
  const repositoryContract = createSiloRepositoryContract({ protocolInstance, provider: clientProvider });

  const isSiloPaused = (address: ChecksumAddress) => {
    return repositoryContract.isSiloPaused(siloAddress, address);
  };

  const allAssets = await siloContract.getAssets();

  /**
   * Short circuit as soon as we figure out one of the assets is paused, as that
   * means the silo is paused.
   */
  return Promise.any(allAssets.map((asset) => isSiloPaused(getChecksumAddressOrThrow(asset))).map(wrapPromise)).catch(
    () => {
      // if we get an AggregateError, then none of the assets are paused, and we can return false
      return false;
    }
  );
};

const wrapPromise = async (promise: Promise<boolean>): Promise<true> => {
  const value = await promise;

  if (value) {
    return true;
  } else {
    throw new Error();
  }
};

export const useIsPaused = (siloAddress: ChecksumAddress, refetchOnMount = false, enabled = true): boolean => {
  const { protocolInstance, clientProvider } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const { frozen } = getConfigurationForSilo(siloAddress);

  const { data } = useQuery([isPausedKey, clientProvider!, protocolInstance!, siloAddress], queryIsPaused, {
    refetchOnMount,
    enabled: !!clientProvider && !!protocolInstance && enabled,
  });

  return !!data || frozen;
};
